import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import {
  AUDIENCE_TYPES,
  PRICING_SCOPE,
  PRICING_STATUS,
  commerceMeta,
  commerceOptions,
  type PricingRule,
} from "@/lib/adminCommerce";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate, formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminPricing() {
  const [rows, setRows] = useState<PricingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<PricingRule>("pricing_rules", "label"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load pricing rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<PricingRule>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("pricing_rules", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (scopeFilter !== "all" && row.scope !== scopeFilter) return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.label, row.target || "", row.audience || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, scopeFilter, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<PricingRule>[] = [
    {
      key: "label",
      header: "Rule",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.label}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.target || "—"}</p>
        </div>
      ),
    },
    { key: "scope", header: "Scope", render: (row) => <StatusPill {...commerceMeta(PRICING_SCOPE, row.scope)} /> },
    {
      key: "audience",
      header: "Audience",
      render: (row) => (
        <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full capitalize">
          {row.audience || "—"}
        </span>
      ),
    },
    {
      key: "price",
      header: "Price",
      render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.price)}</span>,
    },
    {
      key: "child",
      header: "Child",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">
          {row.child_price ? formatRupees(row.child_price) : "—"}
        </span>
      ),
    },
    {
      key: "group",
      header: "Group",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">
          {row.group_price ? formatRupees(row.group_price) : "—"}
        </span>
      ),
    },
    {
      key: "valid",
      header: "Valid",
      render: (row) => (
        <span className="text-xs text-foreground-500 whitespace-nowrap">
          {formatDate(row.date_from)} – {formatDate(row.date_to)}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...commerceMeta(PRICING_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Experiences"
        title="Pricing"
        subtitle="Central pricing rules across experiences and packages — adult, child, group, school, corporate, weekend, seasonal and festival rates."
      />

      <StatCards
        stats={[
          { label: "Total rules", value: rows.length, icon: "ri-price-tag-3-line", tone: "primary" },
          {
            label: "Active",
            value: rows.filter((row) => row.status === "active").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "Seasonal / festival",
            value: rows.filter((row) => row.audience === "seasonal" || row.audience === "festival").length,
            icon: "ri-calendar-event-line",
            tone: "accent",
          },
          {
            label: "Paused / expired",
            value: rows.filter((row) => row.status !== "active").length,
            icon: "ri-pause-circle-line",
            tone: "secondary",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search pricing rules…" />
          <Select
            label="Scope"
            value={scopeFilter}
            onChange={setScopeFilter}
            options={[{ value: "all", label: "All scopes" }, ...commerceOptions(PRICING_SCOPE)]}
          />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...commerceOptions(PRICING_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} rules</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading pricing rules…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-price-tag-3-line"
            title={rows.length === 0 ? "No pricing rules yet" : "No rules match"}
            message="Pricing rules you define will appear here."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.label || "Pricing rule"}
        subtitle="Pricing rule"
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...commerceMeta(PRICING_SCOPE, selected.scope)} />
              <StatusPill {...commerceMeta(PRICING_STATUS, selected.status)} />
            </div>

            <DetailList
              items={[
                { icon: "ri-focus-3-line", label: "Target", value: selected.target || "—" },
                { icon: "ri-user-3-line", label: "Audience", value: selected.audience || "—" },
                { icon: "ri-price-tag-3-line", label: "Adult price", value: formatRupees(selected.price) },
                { icon: "ri-parent-line", label: "Child price", value: selected.child_price ? formatRupees(selected.child_price) : "—" },
                { icon: "ri-group-line", label: "Group price", value: selected.group_price ? formatRupees(selected.group_price) : "—" },
                { icon: "ri-calendar-line", label: "Valid from", value: formatDate(selected.date_from) },
                { icon: "ri-calendar-check-line", label: "Valid to", value: formatDate(selected.date_to) },
              ]}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Audience">
                <select
                  value={selected.audience || "adult"}
                  onChange={(event) => update(selected.id, { audience: event.target.value })}
                  className={fieldInput}
                >
                  {AUDIENCE_TYPES.map((audience) => (
                    <option key={audience} value={audience}>
                      {audience}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value }, "Pricing rule updated.")}
                  className={fieldInput}
                >
                  {commerceOptions(PRICING_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Adult price (₹)">
                <input
                  type="number"
                  value={selected.price}
                  onChange={(event) => update(selected.id, { price: Number(event.target.value) })}
                  className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </Field>
              <Field label="Child price (₹)">
                <input
                  type="number"
                  value={selected.child_price}
                  onChange={(event) => update(selected.id, { child_price: Number(event.target.value) })}
                  className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </Field>
              <Field label="Group price (₹)">
                <input
                  type="number"
                  value={selected.group_price}
                  onChange={(event) => update(selected.id, { group_price: Number(event.target.value) })}
                  className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </Field>
            </div>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}